import React, { memo, useCallback, useMemo } from "react";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import ProductFieldDisplay from "./ProductFieldDisplay";

import { getProductDisplayData } from "../../utils";

import { ReactComponent as LoadingDots } from "../../../../../assets/images/common/LoadingDots.svg";
import watermelon from "../../../../../assets/images/EmptyState_V2.png";

import "./ProductSuggestionList.css";
import "./SpecDisplayTable.css";

function ProductSuggestionItem({
  product,
  fields,
  linkReportJsonFields,
  searchTerm,
  isSelected,
  isNonRemovable,
  loading,
  onAddProduct,
  onRemoveProduct,
}) {
  const { titleToValue, fullText } = getProductDisplayData(product, fields, linkReportJsonFields);

  const handleClick = useCallback(() => {
    if (loading) return;
    if (isSelected) {
      if (!isNonRemovable && onRemoveProduct) onRemoveProduct(product);
    } else {
      onAddProduct(product);
    }
  }, [loading, isSelected, isNonRemovable, product, onAddProduct, onRemoveProduct]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") handleClick();
  }, [handleClick]);

  const className = isSelected
    ? `ProductSuggestionList__DisabledSuggestion${isNonRemovable ? " ProductSuggestionList__NonRemovable" : ""}`
    : "ProductSuggestionList__UnselectedSuggestion";

  return (
    <StyledTooltip
      title={fullText}
      placement="right"
      className="SpecManagerProductTooltip"
      getPopupContainer={(trigger) => trigger?.closest(".ant-drawer-body") || trigger?.closest(".ant-modal-body") || document.body}
    >
      <li
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={className}
        role="option"
        tabIndex={0}
        aria-selected={isSelected}
      >
        <div className="ProductSuggestionList__ItemContent">
          <ProductFieldDisplay
            titleToValue={titleToValue}
            searchTerm={searchTerm}
            rowClassName="ProductSuggestionList__FieldRow"
          />
        </div>
      </li>
    </StyledTooltip>
  );
}

const MemoizedProductSuggestionItem = memo(ProductSuggestionItem);

/**
 * Searchable product suggestion list.
 * Uses dynamic linked fields when available.
 * Memoized items reduce re-renders for large product directories.
 */
function ProductSuggestionList(props) {
  const {
    productSuggestionsScrollbar,
    productSuggestions,
    selectedProductIds,
    nonRemovableProductIds = new Set(),
    searchTerm,
    isLoading,
    loading,
    onAddProduct,
    onRemoveProduct,
    fields = [],
    linkReportJsonFields = [],
  } = props;

  const selectedIdsSet = useMemo(
    () => (selectedProductIds instanceof Set ? selectedProductIds : new Set(selectedProductIds || [])),
    [selectedProductIds],
  );

  const isSelected = useCallback(
    (productId) => selectedIdsSet.has(productId),
    [selectedIdsSet],
  );

  const nonRemovableSet = useMemo(
    () => (nonRemovableProductIds instanceof Set ? nonRemovableProductIds : new Set(nonRemovableProductIds || [])),
    [nonRemovableProductIds],
  );

  const isNonRemovable = useCallback(
    (productId) => nonRemovableSet.has(productId),
    [nonRemovableSet],
  );

  if (isLoading) {
    return (
      <div className="ProductSuggestionList__ProductList">
        <div className="ProductSuggestionList__Loading">
          <LoadingDots width={28} height={28} fill="#19305A" />
        </div>
      </div>
    );
  }

  if (productSuggestions.length === 0) {
    return (
      <div className="ProductSuggestionList__ProductList">
        <div className="ProductSuggestionList__NoResult">
          <img
            className="ProductSuggestionList__NoResultWatermelonImg"
            alt="no result"
            src={watermelon}
          />
          <div className="ProductSuggestionList__NoResultErrorMessage">
            No Products Found
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ProductSuggestionList__ProductList">
      <StyledScrollbar id="ProductSuggestionList__ScrollBar" ref={productSuggestionsScrollbar}>
        <ul className="ProductSuggestionList__SmartSuggestionsList" role="listbox">
          {productSuggestions.map((product) => (
            <MemoizedProductSuggestionItem
              key={product.product_id}
              product={product}
              fields={fields}
              linkReportJsonFields={linkReportJsonFields}
              searchTerm={searchTerm}
              isSelected={isSelected(product.product_id)}
              isNonRemovable={isNonRemovable(product.product_id)}
              loading={loading}
              onAddProduct={onAddProduct}
              onRemoveProduct={onRemoveProduct}
            />
          ))}
        </ul>
      </StyledScrollbar>
    </div>
  );
}

export default memo(ProductSuggestionList);
